import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Buildings, CheckCircle, XCircle, Phone, EnvelopeSimple, MapPin, Clock, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = {
  pharmacy: "Pharmacy", vet: "Veterinary", dental: "Dental", skin_care: "Skin Care",
  cosmetic_tattoos: "Cosmetic Tattoo", gp_clinic: "GP Clinic", specialist_clinic: "Specialist", other: "Other"
};

const TYPE_TO_DEFAULT_CATS = {
  pharmacy: ["simple_compounding", "complex_compounding"],
  vet: ["vet"],
  dental: ["dental"],
  skin_care: ["skin_care"],
  cosmetic_tattoos: ["cosmetic_tattoos", "skin_care"],
  gp_clinic: ["simple_compounding", "complex_compounding"],
  specialist_clinic: ["complex_compounding"],
  other: [],
};

export default function AdminApprovalsPage() {
  const [pending, setPending] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [reviewing, setReviewing] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [allCats, setAllCats] = useState(false);
  const [allowed, setAllowed] = useState([]);
  const [verifyAddrs, setVerifyAddrs] = useState(true);

  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, membersRes, catsRes] = await Promise.all([
        axios.get(`${API}/admin/pending-orgs`, { withCredentials: true }),
        axios.get(`${API}/admin/pending-members`, { withCredentials: true }),
        axios.get(`${API}/categories`, { withCredentials: true }),
      ]);
      setPending(pendingRes.data.organizations || []);
      setPendingMembers(membersRes.data.pending_members || []);
      setCategories(catsRes.data.categories || []);
    } catch (e) { toast.error("Failed to load approvals"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const approveMember = async (m) => {
    try {
      await axios.post(`${API}/orgs/${m.org_id}/memberships/${m.id}/approve`, {}, { withCredentials: true });
      toast.success(`${m.user?.name || m.user?.email} approved for ${m.org?.name}`);
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };

  const rejectMember = async (m) => {
    try {
      await axios.post(`${API}/orgs/${m.org_id}/memberships/${m.id}/reject`, { reason: "" }, { withCredentials: true });
      toast.success("Request declined");
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openReview = (org) => {
    setReviewing(org);
    setAllCats(false);
    // Pre-tick categories based on clinic type
    setAllowed(TYPE_TO_DEFAULT_CATS[org.type] || []);
    setVerifyAddrs(true);
    setShowReview(true);
  };

  const toggleCat = (key) => {
    setAllowed(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleApprove = async () => {
    if (!reviewing) return;
    try {
      const { data } = await axios.post(`${API}/admin/orgs/${reviewing.id}/approve`, {
        all_categories: allCats, allowed_categories: allCats ? [] : allowed, verify_addresses: verifyAddrs
      }, { withCredentials: true });
      toast.success(`Approved! Org code: ${data.code}`);
      setShowReview(false);
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };

  const openReject = (org) => { setReviewing(org); setRejectReason(""); setShowReject(true); };

  const handleReject = async () => {
    if (!reviewing) return;
    try {
      await axios.post(`${API}/admin/orgs/${reviewing.id}/reject`, { reason: rejectReason }, { withCredentials: true });
      toast.success("Rejected");
      setShowReject(false);
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-approvals-page">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">ADMIN</p>
        <h1 className="font-[Chivo] text-3xl font-black tracking-tighter text-zinc-950 mt-1">Pending Approvals</h1>
        <p className="text-sm text-zinc-500 mt-1">{pending.length} clinic{pending.length === 1 ? "" : "s"} &middot; {pendingMembers.length} member request{pendingMembers.length === 1 ? "" : "s"} awaiting review</p>
      </div>

      {/* Pending member (join clinic) requests */}
      {pendingMembers.length > 0 && (
        <div className="mb-8" data-testid="pending-members-section">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus size={18} className="text-[#0055FF]" />
            <h2 className="font-[Chivo] text-lg font-bold text-zinc-950">Member Join Requests</h2>
          </div>
          <div className="space-y-3" data-testid="pending-members-list">
            {pendingMembers.map(m => (
              <div key={m.id} className="border border-zinc-200 p-4 flex flex-wrap items-center justify-between gap-4" data-testid={`pending-member-${m.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-zinc-950">{m.user?.name || "—"}</p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-zinc-600">
                    <span className="flex items-center gap-1.5"><EnvelopeSimple size={13} className="text-zinc-400" />{m.user?.email}</span>
                    <span className="flex items-center gap-1.5"><Phone size={13} className="text-zinc-400" />{m.user?.phone || "—"}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1.5">
                    Wants to join <span className="font-semibold text-zinc-700">{m.org?.name}</span>
                    {m.org?.code ? <span className="font-mono text-zinc-400"> · {m.org.code}</span> : null}
                    {m.requested_at ? <span className="text-zinc-400"> · {new Date(m.requested_at).toLocaleString()}</span> : null}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => approveMember(m)} className="bg-[#0055FF] text-white font-semibold px-4 py-2 text-sm hover:bg-[#0044CC] transition-colors flex items-center gap-1.5" data-testid={`approve-member-${m.id}`}>
                    <CheckCircle size={15} weight="fill" /> Approve
                  </button>
                  <button onClick={() => rejectMember(m)} className="border border-zinc-300 text-zinc-700 font-semibold px-4 py-2 text-sm hover:bg-zinc-50 transition-colors flex items-center gap-1.5" data-testid={`reject-member-${m.id}`}>
                    <XCircle size={15} /> Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <p className="font-mono text-sm text-zinc-500 text-center py-12">LOADING...</p>
      ) : (pending.length === 0 && pendingMembers.length === 0) ? (
        <div className="border border-zinc-200 p-12 text-center" data-testid="no-pending-state">
          <CheckCircle size={48} weight="duotone" className="mx-auto text-emerald-500 mb-3" />
          <p className="font-[Chivo] text-lg font-bold text-zinc-950">All caught up</p>
          <p className="text-sm text-zinc-500 mt-1">No clinic applications or member requests pending review.</p>
        </div>
      ) : pending.length > 0 ? (
        <div className="space-y-4" data-testid="pending-list">
          <div className="flex items-center gap-2">
            <Buildings size={18} className="text-[#0055FF]" />
            <h2 className="font-[Chivo] text-lg font-bold text-zinc-950">Clinic Applications</h2>
          </div>
          {pending.map(org => (
            <div key={org.id} className="border border-zinc-200 hover:border-zinc-300 transition-colors" data-testid={`pending-org-${org.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                {/* Org info */}
                <div className="p-5 md:col-span-2 border-b md:border-b-0 md:border-r border-zinc-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Buildings size={16} className="text-[#0055FF]" />
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">{TYPE_LABEL[org.type] || org.type}</span>
                  </div>
                  <h3 className="font-[Chivo] text-xl font-black text-zinc-950">{org.name}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 text-sm">
                    <div className="flex items-center gap-2 text-zinc-700"><EnvelopeSimple size={14} className="text-zinc-400" />{org.applicant?.email}</div>
                    <div className="flex items-center gap-2 text-zinc-700"><Phone size={14} className="text-zinc-400" />{org.applicant?.phone || "—"}</div>
                    <div className="flex items-center gap-2 text-zinc-700 col-span-1 sm:col-span-2"><span className="font-mono text-xs uppercase tracking-wider text-zinc-400">APPLICANT:</span>{org.applicant?.name}</div>
                  </div>
                  {(org.addresses || []).map(a => (
                    <div key={a.id} className="mt-3 p-3 bg-zinc-50 border border-zinc-200 flex items-start gap-2 text-sm">
                      <MapPin size={14} className="text-zinc-400 mt-0.5 shrink-0" />
                      <div className="text-zinc-700">
                        <p>{a.street_line1}{a.street_line2 ? `, ${a.street_line2}` : ""}</p>
                        <p>{a.suburb}, {a.state} {a.postcode}</p>
                      </div>
                    </div>
                  ))}
                  <p className="font-mono text-xs text-zinc-400 mt-3 flex items-center gap-1"><Clock size={12} />Submitted {new Date(org.created_at).toLocaleString()}</p>
                </div>

                {/* Actions */}
                <div className="p-5 flex flex-col gap-2 justify-center bg-zinc-50">
                  <button onClick={() => openReview(org)} className="bg-[#0055FF] text-white font-semibold py-2.5 hover:bg-[#0044CC] transition-colors flex items-center justify-center gap-2" data-testid={`approve-btn-${org.id}`}>
                    <CheckCircle size={16} weight="fill" /> Review & Approve
                  </button>
                  <button onClick={() => openReject(org)} className="border border-zinc-300 text-zinc-700 font-semibold py-2.5 hover:bg-white transition-colors flex items-center justify-center gap-2" data-testid={`reject-btn-${org.id}`}>
                    <XCircle size={16} /> Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Review/Approve Dialog */}
      <Dialog open={showReview} onOpenChange={setShowReview}>
        <DialogContent className="sm:max-w-lg" data-testid="approve-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Approve Clinic</DialogTitle></DialogHeader>
          {reviewing && (
            <div className="space-y-4">
              <div className="p-3 border border-zinc-200 bg-zinc-50">
                <p className="text-sm font-bold text-zinc-950">{reviewing.name}</p>
                <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-0.5">{TYPE_LABEL[reviewing.type] || reviewing.type}</p>
              </div>

              <label className="flex items-center gap-3 p-2 border border-zinc-200 cursor-pointer hover:bg-zinc-50">
                <Checkbox checked={allCats} onCheckedChange={c => setAllCats(!!c)} data-testid="approve-all-cats-toggle" />
                <div>
                  <p className="text-sm font-medium text-zinc-800">Grant access to ALL categories</p>
                  <p className="text-xs text-zinc-500">Useful for partner pharmacies. Otherwise scope below.</p>
                </div>
              </label>

              {!allCats && (
                <div data-testid="approve-cats-list">
                  <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Allowed Categories</Label>
                  <p className="text-xs text-zinc-400 mt-1 mb-2">Pre-selected based on clinic type. Adjust as needed.</p>
                  <div className="grid grid-cols-2 gap-2">
                    {categories.map(c => (
                      <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 border border-zinc-200 cursor-pointer hover:bg-zinc-50" data-testid={`approve-cat-${c.key}`}>
                        <Checkbox checked={allowed.includes(c.key)} onCheckedChange={() => toggleCat(c.key)} />
                        <span className="text-xs text-zinc-700">{c.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={verifyAddrs} onCheckedChange={c => setVerifyAddrs(!!c)} data-testid="verify-addrs-toggle" />
                <span className="text-zinc-700">Verify submitted address(es) — orders can ship here</span>
              </label>

              <div className="p-3 bg-blue-50 border border-blue-200 text-xs text-blue-800">
                A unique numeric org code will be auto-generated and shown to the clinic owner so they can invite their team.
              </div>

              <button onClick={handleApprove} className="w-full bg-[#0055FF] text-white font-semibold py-3 hover:bg-[#0044CC] transition-colors" data-testid="confirm-approve-button">
                APPROVE & GENERATE CODE
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent className="sm:max-w-md" data-testid="reject-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Reject Application</DialogTitle></DialogHeader>
          {reviewing && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-600">Reject application for <strong>{reviewing.name}</strong>?</p>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Reason (optional, sent to applicant)</Label>
                <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} className="mt-1.5 w-full border border-zinc-300 p-2 text-sm rounded-none focus:border-zinc-950 focus:outline-none" placeholder="e.g. Could not verify clinic registration" data-testid="reject-reason-input" />
              </div>
              <button onClick={handleReject} className="w-full bg-red-600 text-white font-semibold py-3 hover:bg-red-700 transition-colors" data-testid="confirm-reject-button">REJECT</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
